// Izobraževalno-prodajni članek za potencialne najemnike prostora na kupish.si.
// Tema: dropshipping in sodobni prodajni modeli.

export const dropshippingArticle = {
  slug: "dropshipping-za-zacetnike-slovenija",
  title: "Dropshipping za začetnike: kako začeti prodajati brez lastne zaloge",
  section: "unikatne-vsebine" as const,
  date: "2026-09-06 09:00:00",
  excerpt:
    "Praktičen vodnik po dropshippingu v Sloveniji: izbira niše in dobaviteljev, dobavni roki, obdelava naročil, zakonske obveznosti in kako ponudbo preizkusiti brez dragega oglaševanja.",
  content: `
<p><strong>Dropshipping</strong> je prodajni model, pri katerem izdelka nimate na zalogi. Ko kupec odda naročilo, ga posredujete dobavitelju, ta pa izdelek odpošlje neposredno kupcu. Vi skrbite za ponudbo, ceno, predstavitev in odnos s kupcem — ne pa za skladišče, embaliranje in pošiljanje.</p>

<p>Model je priljubljen pri dveh skupinah: pri začetnikih, ki želijo preizkusiti trg brez vlaganja v zalogo, in pri obstoječih trgovcih, ki želijo razširiti ponudbo z izdelki, ki jih sicer ne bi kupili na zalogo. Ta vodnik pojasni, kako se dropshippinga lotiti resno — in kje se najpogosteje zalomi.</p>

<h2>Kako dropshipping dejansko deluje</h2>

<p>Postopek je v vseh primerih enak, ne glede na to, ali prodajate prek lastne trgovine ali prek prostora na platformi:</p>

<ol>
<li>Z dobaviteljem se dogovorite o ceni, dobavnih rokih in pogojih vračila.</li>
<li>Izdelke objavite s svojimi fotografijami, opisi in prodajno ceno.</li>
<li>Kupec naroči pri vas in plača vam.</li>
<li>Naročilo posredujete dobavitelju in mu plačate nabavno ceno.</li>
<li>Dobavitelj pošlje izdelek kupcu, praviloma brez svojih promocijskih materialov.</li>
<li>Za kupca ste ves čas <strong>vi</strong> prodajalec — tudi pri reklamaciji.</li>
</ol>

<p>Zadnja točka je najpomembnejša in jo začetniki najpogosteje spregledajo. Zakonska odgovornost do kupca je vaša. Dobavitelj je vaš poslovni partner, ne pa naslov, na katerega bi lahko preusmerili nezadovoljno stranko.</p>

<h2>Prednosti in slabosti brez olepševanja</h2>

<h3>Kaj model dejansko prinaša</h3>

<ul>
<li><strong>Nizek vstopni vložek.</strong> Ne kupujete zaloge, zato ne tvegate denarja za izdelke, ki se ne prodajo.</li>
<li><strong>Široka ponudba.</strong> Ponudbo lahko razširite z desetinami izdelkov, ne da bi potrebovali skladišče.</li>
<li><strong>Hitro preizkušanje.</strong> Nov izdelek objavite v pol ure in v nekaj tednih veste, ali obstaja povpraševanje.</li>
<li><strong>Prilagodljivost.</strong> Sezonsko ponudbo dodate in odstranite brez posledic za zalogo.</li>
</ul>

<h3>Kaj model odvzame</h3>

<ul>
<li><strong>Nižje marže.</strong> Ker kupujete po kosu, so nabavne cene višje kot pri nakupu palete.</li>
<li><strong>Manj nadzora.</strong> Dobavni rok, embalaža in kakovost pakiranja niso v vaših rokah.</li>
<li><strong>Odvisnost.</strong> Če dobavitelj razproda izdelek ali dvigne ceno, se to takoj pozna pri vas.</li>
<li><strong>Zahtevnejša podpora.</strong> Vsako vprašanje o pošiljki morate preveriti pri tretji osebi.</li>
</ul>

<p>Dropshipping torej ni pot brez dela. Je pot brez zaloge — kar ni isto.</p>

<h2>Izbira niše: kje se odloči uspeh</h2>

<p>Največ začetnikov propade, ker izberejo izdelke, ki so povsod. Če isti izdelek prodaja sto trgovin, se prodaja odloči izključno po ceni, in tekmo dobi tisti z največjim volumnom. Vi to niste.</p>

<p>Dobra niša za slovenski trg ima praviloma naslednje značilnosti:</p>

<ul>
<li><strong>Jasno ciljno skupino.</strong> Vaš kupec se prepozna v enem stavku: »oprema za male živali«, »pripomočki za peko«, »avtokozmetika za ljubitelje«.</li>
<li><strong>Ponavljajoč nakup ali dopolnitve.</strong> Kupec, ki se vrne, je vreden nekajkrat več od enkratnega.</li>
<li><strong>Razumno razmerje cena/dostava.</strong> Izdelek za 8 EUR z dostavo 5 EUR se ne prodaja dobro.</li>
<li><strong>Nizko tveganje vračil.</strong> Oblačila z veliko velikostmi imajo bistveno višjo stopnjo vračil kot pripomočki.</li>
<li><strong>Možnost razlage.</strong> Če lahko o izdelku napišete koristen nasvet, imate prednost pred trgovcem, ki ga zgolj navaja.</li>
</ul>

<p><strong>Praktičen preizkus:</strong> če o izdelku ne znate napisati 300 besed koristne vsebine, verjetno tudi ne znate prepričati kupca.</p>

<h2>Kako izbrati zanesljivega dobavitelja</h2>

<p>Dobavitelj je vaš najbolj tvegan del posla. Pred prvim naročilom preverite naslednje:</p>

<ul>
<li><strong>Lokacija skladišča.</strong> Evropsko skladišče pomeni dostavo v nekaj dneh, oddaljeno pa lahko tri tedne.</li>
<li><strong>Realni dobavni roki.</strong> Ne verjemite oglaševanemu roku — naročite vzorec nase in izmerite čas.</li>
<li><strong>Kakovost izdelka in embalaže.</strong> Vzorec razpakirajte tako, kot bi ga kupec.</li>
<li><strong>Politika vračil.</strong> Kdo krije stroške, v kakšnem roku, na kateri naslov.</li>
<li><strong>Odzivnost.</strong> Pošljite vprašanje in izmerite, v koliko urah dobite odgovor. Tako se bo odzival tudi takrat, ko boste imeli težavo s stranko.</li>
<li><strong>Dokumentacija.</strong> Računi, izjave o skladnosti, navodila v slovenščini za izdelke, kjer so obvezna.</li>
</ul>

<p>Idealno je, da imate za pomemben izdelek <strong>dva dobavitelja</strong>. En sam dobavitelj pomeni, da je vaša prodaja ustavljena v trenutku, ko on razproda zalogo.</p>

<h2>Dobavni roki: največji razlog za slabe ocene</h2>

<p>Slovenski kupec pričakuje pošiljko v nekaj dneh. Če je vaš dobavni rok daljši, to ni nujno težava — <strong>težava je, če tega ne poveste vnaprej</strong>. Nezadovoljstvo skoraj nikoli ne izvira iz dolgega roka, temveč iz neizpolnjenega pričakovanja.</p>

<p>Zato pri vsakem izdelku jasno navedite predviden rok dobave, pri daljših rokih pa ga ponovite tudi v potrditvi naročila. Kupcu sporočite, ko pošiljka odide, in mu posredujte številko za sledenje. To je najcenejši ukrep za zmanjšanje števila reklamacij.</p>

<h2>Obdelava naročil in zaloge</h2>

<p>Dokler imate nekaj naročil dnevno, jih obvladate ročno. Ko jih je več, potrebujete red:</p>

<ul>
<li><strong>Enotna preglednica naročil</strong> s statusi: prejeto, posredovano dobavitelju, odposlano, zaključeno.</li>
<li><strong>Redno preverjanje zalog.</strong> Izdelek, ki ga dobavitelj nima, mora s ponudbe isti dan.</li>
<li><strong>Pripravljena besedila</strong> za pogosta sporočila (potrditev, obvestilo o odpremi, zamuda, vračilo).</li>
<li><strong>Ločen pregled marž.</strong> Nabavna cena, dostava, provizija plačila in vračila — šele razlika je vaš zaslužek.</li>
</ul>

<p><strong>Pravilo:</strong> vsak korak, ki ga ponovite več kot desetkrat na teden, si zapišite v postopek. Šele ko je postopek zapisan, ga lahko avtomatizirate ali predate nekomu drugemu.</p>

<h2>Pravni okvir: kaj morate urediti v Sloveniji</h2>

<p>Dropshipping ni poseben pravni status. Za kupca ste navaden spletni prodajalec, zato veljajo enaka pravila kot za vsako spletno trgovino:</p>

<ul>
<li><strong>Registrirana dejavnost</strong> (najpogosteje s. p. ali d. o. o.) in izdajanje računov skladno z davčnimi predpisi.</li>
<li><strong>Pravica do odstopa v 14 dneh</strong> pri prodaji potrošnikom, z jasnim opisom postopka vračila.</li>
<li><strong>Jamstvo za skladnost blaga</strong> — reklamacijo rešujete vi, ne dobavitelj.</li>
<li><strong>Pošteno oglaševanje</strong> — brez izmišljenih popustov, lažnih zalog in odštevalnikov, ki se vsak dan ponastavijo.</li>
<li><strong>Varstvo osebnih podatkov</strong> pri posredovanju naslova kupca dobavitelju.</li>
<li><strong>Uvoz iz tretjih držav</strong> pomeni carinske in davčne obveznosti; pri dostavi iz EU teh zapletov ni.</li>
</ul>

<p>Če se le da, začnite z <strong>dobavitelji iz Evropske unije</strong>. Krajši roki, manj carinskih vprašanj in bistveno manj neprijetnih presenečenj za kupca.</p>

<h2>Zakaj sama spletna trgovina ne prinese prodaje</h2>

<p>Najbolj boleča lekcija dropshippinga ni logistika, temveč obisk. Nova spletna trgovina nima obiskovalcev. Nima zgodovine v iskalnikih, nima mnenj, nima prepoznavnosti. Tudi popolna ponudba ostane nevidna.</p>

<p>V praksi to pomeni dvoje: mesece dela na vsebinah, da vas iskalnik sploh začne prikazovati, ali plačano oglaševanje. Ker so cene oglasov iz leta v leto višje, pri nizkih maržah dropshippinga pogosto pojedo ves zaslužek. Prodajalec, ki plača 6 EUR za obiskovalca in ima 9 EUR marže, posla nima — ima drago hobi dejavnost.</p>

<h2>Preizkus ponudbe na kupish.si</h2>

<p>Prav zato je za dropshipping smiselno začeti tam, kjer obiskovalci že so. Na <a class="decorated-link" href="/">kupish.si</a> najamete prostor v spletnem nakupovalnem središču, kjer kupci že brskajo po ponudbi različnih prodajalcev.</p>

<ul>
<li><strong>Izdelke objavite sami</strong> in jih kadar koli zamenjate — testiranje niše stane le vaš čas.</li>
<li><strong>Tehnika je urejena:</strong> gostovanje, varnost, prikaz na mobilnih napravah, hitrost strani.</li>
<li><strong>Skupna promocija</strong> središča privablja obiskovalce za vse prodajalce hkrati.</li>
<li><strong>Poštnino določite sami</strong> pri vsakem izdelku, skladno z dogovorom z dobaviteljem.</li>
<li><strong>Brez provizije od prodaje</strong> — plačate letni najem prostora, marža ostane vaša.</li>
</ul>

<p>Praktičen pristop: objavite 10 do 20 izdelkov iz ene niše, spremljajte, kateri prejemajo ogledov in povpraševanj, in po enem mesecu ponudbo prevetrite. Tako v nekaj tednih dobite podatke, za katere bi sicer plačali več sto evrov oglasov.</p>

<h2>Pet napak, ki jih ne ponovite</h2>

<ol>
<li><strong>Kopirani opisi dobavitelja.</strong> Enako besedilo kot pri desetih drugih prodajalcih ne prinese ne uvrstitve ne zaupanja.</li>
<li><strong>Prevzete fotografije slabe kakovosti.</strong> Vsaj glavno fotografijo posnemite ali obdelajte sami.</li>
<li><strong>Prenizka cena.</strong> Marža mora pokriti vračila, provizije in vaš čas, ne le nabavne cene.</li>
<li><strong>Preveč izdelkov naenkrat.</strong> Dvajset dobro predstavljenih prekaša dvesto naloženih na hitro.</li>
<li><strong>Molk po naročilu.</strong> Kupec, ki tri dni nima nobenega sporočila, prvi napiše slabo mnenje.</li>
</ol>

<h2>Izračun marže: kdaj se dropshipping sploh izide</h2>

<p>Marža v dropshippingu ni razlika med nabavno in prodajno ceno. Realna marža je tisto, kar ostane po vseh odbitkih, zato jo izračunajte za vsak izdelek posebej:</p>

<ul>
<li><strong>Nabavna cena</strong> pri dobavitelju, vključno z njegovo dostavo do kupca.</li>
<li><strong>Strošek plačila</strong> — provizija ponudnika kartičnega plačila ali stroški plačilnega prometa.</li>
<li><strong>Stopnja vračil.</strong> Če je vrnjeno vsako deseto naročilo, to zmanjša povprečno maržo na vseh prodanih kosih.</li>
<li><strong>Napake in izgubljene pošiljke.</strong> Računajte majhen, a stalen odstotek izgub — vedno se zgodijo.</li>
<li><strong>Vaš čas.</strong> Obdelava naročila, komunikacija in reševanje težav so delo, ki ga je treba ovrednotiti.</li>
</ul>

<p>Preprosto vodilo: če po vseh odbitkih na naročilu ne ostane vsaj 25 do 30 odstotkov prodajne cene, model ne prenese niti manjših zapletov. Prvo nihanje pri dobavitelju ali val vračil vas potisne v izgubo.</p>

<h2>Kako izbrati prodajno ceno</h2>

<p>Cena naj bo posledica izračuna, ne posnemanja konkurence. Najprej izračunajte spodnjo mejo, ki pokrije vse stroške, nato dodajte maržo, ki ustreza vloženemu delu. Šele potem preverite konkurenco — in če ste dražji, poskrbite za razlog: boljša predstavitev, hitrejši odgovor, jasnejši podatki o dostavi, dopolnilni nasvet.</p>

<p>Nižanje cene je najhitrejša pot do večjega prometa in najhitrejša pot do propada. Kupca, ki pride izključno zaradi najnižje cene, boste izgubili takoj, ko bo nekdo šel evro nižje.</p>

<h2>Od preizkusa do stalne ponudbe</h2>

<p>Dropshipping se najbolj obnese kot <strong>faza preizkušanja</strong>. Ko se izdelek izkaže — redno se prodaja, malo vračil, dobri odzivi — ga je smiselno kupiti na zalogo. Nabavna cena bo nižja, dostava hitrejša, nadzor nad kakovostjo pa vaš.</p>

<p>Tako sčasoma nastane zdrava kombinacija: nekaj deset preverjenih izdelkov na zalogi, okoli njih pa širša ponudba prek dobaviteljev, ki jo prilagajate sezoni in povpraševanju.</p>

<h2>Pogosta vprašanja</h2>

<h3>Ali je dropshipping v Sloveniji zakonit?</h3>
<p>Da. Je običajen prodajni model. Potrebujete registrirano dejavnost, urejeno izdajanje računov in spoštovanje pravil varstva potrošnikov.</p>

<h3>Koliko denarja potrebujem za začetek?</h3>
<p>Bistveno manj kot pri klasični trgovini, saj ne kupujete zaloge. Računajte na stroške vzorcev, fotografij, najema prostora in nekaj rezerve za vračila.</p>

<h3>Ali lahko dropshipping združim z lastno zalogo?</h3>
<p>Da, in to je pogosto najboljša kombinacija. Najbolje prodajane izdelke dajte na zalogo, ostale pa ponujajte prek dobavitelja.</p>

<h3>Kako hitro lahko začnem prodajati?</h3>
<p>Če imate urejeno dejavnost in dogovor z dobaviteljem, lahko prve izdelke objavite v enem dnevu.</p>

<h2>Zaključek: najprej preizkusite, nato vlagajte</h2>

<p>Dropshipping je odlično orodje za preizkušanje trga in slabo orodje za slepo posnemanje. Uspejo tisti, ki izberejo ozko nišo, izberejo zanesljivega dobavitelja, pošteno komunicirajo roke in izdelke predstavijo bolje od konkurence.</p>

<p>Če želite svoje izdelke preizkusiti brez dragega oglaševanja, si oglejte pogoje na strani <a class="decorated-link" href="/najem-spletne-trgovine">Najem spletne trgovine</a> in uredite <a class="decorated-link" href="/placilo-najemnine">plačilo najemnine</a>. Prve izdelke lahko objavite še ta teden — in v enem mesecu veste, kaj se prodaja.</p>
`,
};
